import type { TFunction } from 'i18next';

const exactErrorKeys: Record<string, string> = {
  '無法載入錢包帳戶資料。': 'errors.loadProfile',
  '無法讀取登入狀態，請重新開啟 App。': 'errors.loginState',
  '登出失敗，請稍後再試。': 'errors.signOut',
  'MetaMask 餘額查詢失敗。': 'errors.metaMaskBalance',
  '交易紀錄查詢失敗。': 'errors.metaMaskActivity',
  'MetaMask 連接失敗。': 'errors.metaMaskConnect',
  'MetaMask 中斷連接失敗。': 'errors.metaMaskDisconnect',
  '價格查詢失敗。': 'errors.priceQuery',
  'BBG 餘額查詢失敗。': 'errors.bbgBalance',
  'F-CASH 餘額查詢失敗。': 'errors.fcashBalance',
  '無法讀取 BBG 領取狀態。': 'errors.claimStatus',
  'BBG 領取失敗。': 'errors.claimFailed',
  '傳送失敗，請稍後再試。': 'errors.sendFailed',
  '兌換失敗，請稍後再試。': 'errors.swapFailed',
  '鏈上餘額查詢失敗': 'errors.onChainBalanceFailed',
  '鏈上交易仍未確認，請稍後查看交易狀態。': 'errors.transactionNotConfirmed',
  '鏈上交易紀錄服務尚未設定。': 'errors.transactionHistoryNotConfigured',
  '鏈上交易紀錄服務暫時無法連線。': 'errors.transactionHistoryOffline',
  '鏈上交易紀錄服務暫時無法使用。': 'errors.transactionHistoryOffline',
  '鏈上交易紀錄服務回傳格式無效。': 'errors.transactionHistoryInvalid',
  '請輸入有效的錢包地址。': 'errors.invalidWalletAddress',
  '請輸入有效的收款錢包地址。': 'errors.invalidRecipientAddress',
  '輸入及輸出代幣不能相同。': 'errors.sameSwapTokens',
  '兌換數量必須大於 0。': 'errors.swapAmountPositive',
  '兌換數量不可超過目前鏈上餘額。': 'errors.swapAmountExceedsBalance',
  '兌換數量超過 Permit2 支援上限。': 'errors.swapAmountPermitLimit',
  '目前無法取得有效報價，請稍後再試。': 'errors.quoteUnavailable',
  '最低輸出數量無效，請調低兌換數量後再試。': 'errors.minimumOutputInvalid',
  'ERC-20 授權交易未成功。': 'errors.erc20ApprovalFailed',
  'Permit2 授權交易未成功。': 'errors.permit2ApprovalFailed',
  '找不到 MetaMask 連接器，請按連接按鈕開啟 MetaMask App。': 'errors.metaMaskConnectorMissing',
  'MetaMask 尚未切換到 World Chain，交易已取消。': 'errors.metaMaskWrongChain',
  'MetaMask 沒有回傳有效的錢包地址。': 'errors.metaMaskWalletMissing',
  '你已取消中斷 MetaMask 連接。': 'errors.metaMaskCancelDisconnect',
  '無法中斷 MetaMask 連接，請在 MetaMask 內手動移除網站連接。': 'errors.metaMaskManualDisconnect',
  '傳送數量必須大於 0。': 'errors.sendAmountPositive',
  '傳送數量不可超過目前鏈上餘額。': 'errors.sendAmountExceedsBalance',
  '價格服務暫時無法連線。': 'errors.priceServiceOffline',
  'Supabase 尚未設定，無法查詢 BBG 遊戲內餘額。': 'errors.supabaseNotConfigured',
  'World App 沒有完成這筆交易。': 'errors.worldAppTransactionIncomplete',
  'World App 回傳了無法辨識的 User Operation 狀態。': 'errors.worldAppUnknownOperation',
  '請在 World App 內開啟 Bubble Wallet 後再進行交易。': 'errors.worldAppNativeTransactionRequired',
  'World App 沒有使用原生 MiniKit 完成交易。': 'errors.worldAppNativeTransactionFailed',
  'World App 沒有回傳 User Operation 編號。': 'errors.worldAppUserOperationMissing',
  'authorization wallet 與目前 World App wallet 不一致。': 'errors.worldAppAuthorizationWalletMismatch',
  'Flight404 Firebase bridge 尚未設定，暫時無法讀取 F-CASH。': 'errors.fcashBalance',
  'Flight404 Firebase project 尚未設定，暫時無法讀取 F-CASH。': 'errors.fcashBalance',
  'Flight404 Firebase 身份橋接失敗。': 'errors.fcashBalance',
  'Flight404 Firebase 身份橋接沒有回傳認證資料。': 'errors.fcashBalance',
  'Firebase Auth 登入失敗。': 'errors.fcashBalance',
  'Firebase Auth 身份與 GGMOMOMO 帳戶不一致。': 'errors.fcashBalance',
  'Flight404 Firebase 身份橋接已過期，請重新查詢。': 'errors.fcashBalance',
  'Bubble authorization 回應格式不正確。': 'errors.claimInvalidAuthorization',
  'authorization.deadline 格式不正確。': 'errors.claimInvalidAuthorization',
  'authorization signature 格式不正確。': 'errors.claimInvalidAuthorization',
  'idempotency_key 格式不正確。': 'errors.claimInvalidAuthorization',
  'authorization patternId 與目前請求不一致。': 'errors.claimInvalidAuthorization',
};

export function localizeError(error: unknown, t: TFunction, fallbackKey = 'errors.generic'): string {
  const message = error instanceof Error && error.message ? error.message : '';
  if (!message) return t(fallbackKey);
  const errorCode = error && typeof error === 'object' && 'code' in error
    ? (error as { code?: unknown }).code
    : null;
  const codeKeys: Record<string, string> = {
    authorization_conflict: 'errors.claimAuthorizationConflict',
    authorization_expired: 'errors.claimAuthorizationExpired',
    authorization_wallet_mismatch: 'errors.claimWalletMismatch',
    backend_unavailable: 'errors.claimBackendUnavailable',
    claim_cooldown: 'errors.claimCooldown',
    cap_exhausted: 'errors.claimCapExhausted',
    claim_status_unavailable: 'errors.claimStatusUnavailable',
    inventory_insufficient: 'errors.claimInventoryInsufficient',
    invalid_authorization: 'errors.claimInvalidAuthorization',
    pattern_not_allowed: 'errors.claimPatternNotAllowed',
    session_continuity_conflict: 'errors.claimSessionConflict',
    transaction_cancelled: 'errors.claimTransactionCancelled',
    transaction_failed: 'errors.claimTransactionFailed',
    world_verification_failed: 'errors.claimWorldVerificationFailed',
    flight404_jwt_expired: 'errors.flight404SessionExpired',
    flight404_bridge_failed: 'errors.fcashBalance',
  };
  if (typeof errorCode === 'string' && codeKeys[errorCode.toLowerCase()]) {
    return t(codeKeys[errorCode.toLowerCase()]);
  }
  const exactKey = exactErrorKeys[message];
  if (exactKey) return t(exactKey);

  const timeoutMatch = message.match(/^(.+) 逾時，請稍後再試。$/);
  if (timeoutMatch) return t('errors.rawTimeout', { label: timeoutMatch[1] });
  const connectionMatch = message.match(/^(.+) 無法連線，請稍後再試。$/);
  if (connectionMatch) return t('errors.rawConnectionFailed', { label: connectionMatch[1] });
  const amountDecimalsMatch = message.match(/^(?:傳送)?數量最多支援 (\d+) 位小數。$/);
  if (amountDecimalsMatch) return t('errors.amountDecimals', { decimals: amountDecimalsMatch[1] });
  const priceResponseMatch = message.match(/^價格服務回應失敗（(\d+)）。$/);
  if (priceResponseMatch) return t('errors.priceServiceResponse', { status: priceResponseMatch[1] });
  const worldAppDetailMatch = message.match(/^World App 交易失敗（([^）]+)）：(.+)$/);
  if (worldAppDetailMatch) return t('errors.worldAppTransactionFailedWithoutDetail', { code: worldAppDetailMatch[1] });
  const worldAppCodeMatch = message.match(/^World App 交易失敗（([^）]+)）。$/);
  if (worldAppCodeMatch) return t('errors.worldAppTransactionFailedWithoutDetail', { code: worldAppCodeMatch[1] });
  const worldAppPendingMatch = message.match(/^World App User Operation 尚未確認（([^）]+)），請稍後查看交易狀態。$/);
  if (worldAppPendingMatch) return t('errors.worldAppPending');
  const worldAppSubmittedMatch = message.match(/^World App 交易已提交，但鏈上執行失敗（User Operation：([^）]+)）。$/);
  if (worldAppSubmittedMatch) return t('errors.worldAppSubmittedFailed');
  return t(fallbackKey);
}

function normalizeErrorCode(value: string): string {
  const normalized = value
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^a-zA-Z0-9:_-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();
  return normalized.slice(0, 64) || 'UNKNOWN_ERROR';
}

function codeFromFallbackKey(fallbackKey: string): string {
  const key = fallbackKey.split('.').at(-1) ?? 'generic';
  const code = normalizeErrorCode(key);
  return code === 'GENERIC' ? 'UNKNOWN_ERROR' : code;
}

export function getErrorCode(error: unknown, fallbackCode = 'UNKNOWN_ERROR'): string {
  if (error && typeof error === 'object') {
    const candidate = error as {
      error_code?: unknown;
      errorCode?: unknown;
      diagnosticCode?: unknown;
      code?: unknown;
    };
    for (const value of [candidate.error_code, candidate.errorCode, candidate.diagnosticCode, candidate.code]) {
      if (typeof value === 'string' && value.trim()) return normalizeErrorCode(value);
    }
    if (error instanceof Error && error.name && error.name !== 'Error') {
      return normalizeErrorCode(error.name);
    }
  }

  const message = typeof error === 'string'
    ? error
    : error instanceof Error
      ? error.message
      : '';
  const embeddedCode = message.match(/(?:交易失敗|ERROR CODE)\s*[（(:：]\s*([a-zA-Z0-9:_-]+)/i)?.[1];
  return embeddedCode ? normalizeErrorCode(embeddedCode) : normalizeErrorCode(fallbackCode);
}

export function formatMessageWithCode(message: string, t: TFunction, code: string): string {
  return `${message} (${t('errors.errorCode')}: ${normalizeErrorCode(code)})`;
}

export function formatErrorWithCode(
  error: unknown,
  t: TFunction,
  fallbackKey = 'errors.generic',
  fallbackCode?: string,
): string {
  const message = localizeError(error, t, fallbackKey);
  const code = getErrorCode(error, fallbackCode ?? codeFromFallbackKey(fallbackKey));
  return formatMessageWithCode(message, t, code);
}
